import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowUp, FileText, Terminal, Blocks, Sparkles, Link2, FileUp, Paperclip, X, Loader2 } from 'lucide-react';
import YouTubeMark from './YouTubeMark';
import { setPendingFile, setPostLoginRedirect } from '../lib/pendingBuild';
import GithubConnect, { GithubMark } from './GithubConnect';
import StarterGallery from './StarterGallery';
import { useAuth } from '../context/AuthContext';
import { portal } from '../lib/api';

// File types the skills API accepts for document upload (mirrors the generate form).
import { ACCEPT, rejectReason } from '../lib/uploadTypes';

// Chat-first landing. The page lands directly on a conversational composer:
// describe what you want (or paste a YouTube link / article / PDF), pick the
// artifact — a skill package, a CLI + MCP, or both — and Loreto builds it.
// Submitting hands the prompt + artifact mode to the authenticated generate flow.
const MODES = [
  { id: 'skill', Icon: FileText, label: 'Skill package' },
  { id: 'cli', Icon: Terminal, label: 'CLI + MCP' },
  { id: 'both', Icon: Blocks, label: 'Both' },
];

// A worked sample prompt per artifact, shown as a muted placeholder (a
// suggestion, not prefilled text). Each models the pattern — [what to build]
// from [your source] → [what the agent should do] — and the suggestion swaps
// as the user changes the artifact toggle, so they always see a relevant example.
const PLACEHOLDERS = {
  skill: 'Build a skill from this YouTube video: [paste a link] — pull out what it actually teaches so my coding agent can run the workflow on its own.',
  cli: 'Build a CLI from these API docs: [paste a link] — turn each endpoint into a command I can run from Claude Code.',
  both: 'Build a skill and a matching CLI from this guide: [paste a link] — so my agent can read the steps and run them.',
};

// The same suggestion with the explanatory half removed. Two rows of text is
// all a phone has before the toolbar, and the long version was sliced mid-word
// — the first sentence a visitor reads should not end in an ellipsis it did
// not choose.
const PLACEHOLDERS_NARROW = {
  skill: 'Build a skill from a YouTube link, repo, or article.',
  cli: 'Build a CLI from these API docs: [paste a link]',
  both: 'Build a skill and a CLI from this guide: [paste a link]',
};

const EXAMPLES = [
  {
    Icon: () => <YouTubeMark size={14} />,
    text: 'From a YouTube video',
    mode: 'skill',
    fill: 'Build a skill from this YouTube video: [paste a link] — pull out what it actually teaches so my coding agent can run the workflow on its own.',
  },
  {
    Icon: (p) => <Link2 size={13} className="text-[var(--accent)]" {...p} />,
    text: 'A CLI from API docs',
    mode: 'cli',
    fill: 'Build a CLI from these API docs: [paste a link] — turn each endpoint into a command I can run from Claude Code.',
  },
  {
    // The GitHub mark rather than the generic link glyph, matching the YouTube
    // chip above it: the icon should name the source, not the fact that it is
    // a URL — which is true of every chip here.
    Icon: () => <GithubMark size={13} />,
    text: 'From a GitHub repo',
    mode: 'skill',
    fill: 'Build a skill from this repo: [paste a github.com link] — read its documentation and turn what it teaches into something my agent can run.',
  },
  {
    Icon: (p) => <FileUp size={13} className="text-[var(--accent)]" {...p} />,
    text: 'From your own workflow',
    mode: 'skill',
    fill: 'Build a skill from my own workflow: [describe what you do, step by step] — so my agent can repeat it reliably.',
  },
];

// Rotating eyebrow. Loreto is more than a marketplace — each line surfaces a
// different facet (capture expertise, any source in, run it / sell it), cycling
// every few seconds so the hero teaches breadth without a wall of copy.
const TAGLINES = [
  'The model is fixed — the harness is yours',
  'A skill is the first piece of the harness',
  'A YouTube video becomes a running skill',
  'Your repository already wrote half of it',
  'Customise the harness, not the prompt',
  'Every skill runs as a CLI + MCP',
  'Start with one piece, build the whole harness',
  'A marketplace where agents buy skills',
];

export default function ChatLanding() {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const on = (e) => setNarrow(e.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  const [prompt, setPrompt] = useState('');
  const [mode, setMode] = useState('skill');
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [checking, setChecking] = useState(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthed } = useAuth();


  // Cycle the eyebrow tagline, cross-fading between lines every few seconds.
  const [tagIdx, setTagIdx] = useState(0);
  const [tagShow, setTagShow] = useState(true);
  useEffect(() => {
    const id = setInterval(() => {
      setTagShow(false); // fade out
      window.setTimeout(() => {
        setTagIdx((i) => (i + 1) % TAGLINES.length);
        setTagShow(true); // fade the next line back in
      }, 320);
    }, 3400);
    return () => clearInterval(id);
  }, []);

  const grow = () => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 220) + 'px';
  };

  const onPickFile = (e) => {
    const picked = e.target.files?.[0] || null;
    // `accept` on the input is only a picker hint — "All Files" defeats it — so
    // the rule is enforced here, before the file can be carried to /generate.
    const why = rejectReason(picked);
    if (why) {
      setFileError(why);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      return;
    }
    setFileError(null);
    setFile(picked);
  };

  const submit = async () => {
    if (checking) return;
    const q = prompt.trim();
    const params = new URLSearchParams();
    params.set('artifact', mode);
    if (file) {
      // Hand the file to the generate flow (upload mode); any typed text rides
      // along as extra context to steer extraction.
      setPendingFile(file);
      if (q) params.set('context', q);
    } else if (q) {
      params.set('source', q);
    }
    const target = `/dashboard/generate?${params.toString()}`;

    // Not signed in → send to login first, carrying the prompt (and any file) so
    // it runs as one of their free calls the moment they're back.
    if (!isAuthed) {
      setPostLoginRedirect(target);
      navigate('/dashboard');
      return;
    }

    // Signed in → a non-subscribed user who's used their free calls goes to the
    // paywall instead of the generator. (The backend also enforces this quota.)
    setChecking(true);
    try {
      const u = await portal.usage();
      const ks = u?.key_summaries?.[0];
      const plan = ks?.plan ?? 'free';
      const used = u?.total_this_month ?? 0;
      const limit = ks?.monthly_limit ?? 2;
      if (plan === 'free' && used >= limit) {
        navigate('/pricing');
        return;
      }
    } catch {
      /* usage lookup failed — fall through; the generate call still enforces quota */
    } finally {
      setChecking(false);
    }
    navigate(target);
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  // Drop a worked prompt in and leave the caret exactly where the user has to
  // act. If the prompt carries a [bracketed] placeholder we select it, so the
  // next paste (or keystroke) replaces it instead of landing in the middle of
  // a sentence the reader then has to edit around.
  //
  // The selection has to be applied *after* React commits the new value —
  // writing a controlled textarea's value drops the caret at the end, which
  // undoes anything set in the click handler or a rAF callback. The tick makes
  // the effect fire even when the same example is clicked twice.
  const pendingSel = useRef(null);
  const [applyTick, setApplyTick] = useState(0);
  useEffect(() => {
    const sel = pendingSel.current;
    const el = taRef.current;
    if (!sel || !el) return;
    pendingSel.current = null;
    grow();
    el.focus();
    el.setSelectionRange(sel[0], sel[1]);
  }, [applyTick]); // eslint-disable-line

  const applyExample = (ex) => {
    const m = /\[[^\]]+\]/.exec(ex.fill);
    pendingSel.current = m
      ? [m.index, m.index + m[0].length]
      : [ex.fill.length, ex.fill.length];
    setPrompt(ex.fill);
    if (ex.mode) setMode(ex.mode);
    setApplyTick((t) => t + 1);
  };

  // A card in the gallery fills the box and brings it back into view. The
  // gallery sits below the fold on a laptop, so without the scroll the click
  // appears to do nothing at all.
  const pickStarter = (url) => {
    setPrompt(url);
    setMode('skill');
    const el = taRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // After the scroll, or the browser fights the focus for it.
    window.setTimeout(() => {
      el.focus({ preventScroll: true });
      el.setSelectionRange(url.length, url.length);
      grow();
    }, 320);
  };

  return (
    <section className="relative w-full overflow-hidden grain border-t border-[var(--border)] pt-24 pb-16 sm:pt-0 sm:pb-0" aria-label="Build a skill with Loreto">
      {/* ambient accent glow, biased toward the demo panel */}
      <div
        className="pointer-events-none absolute top-[-15%] right-[-6%] w-[950px] h-[950px] rounded-full opacity-[0.14] blur-none md:blur-[150px]"
        style={{ background: 'radial-gradient(circle, var(--accent) 0%, transparent 60%)' }}
      />
      <div className="absolute bottom-0 left-0 right-0 h-40 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, transparent, var(--bg))' }} />

      {/* One column, full width. The right hand demo panel used to sit here and
          was removed; without changing the grid the copy stays pinned to the
          left half of a 1240px box and the page looks broken rather than
          simplified. */}
      <div className="relative w-full max-w-[1240px] mx-auto px-6 pt-28 pb-20">
        {/* LEFT — headline + composer */}
        <div className="text-left">
          <div className="inline-flex items-center gap-2 mb-7 text-[var(--text-3)] min-h-[1.1em]" aria-live="polite">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)] pulse-dot shrink-0" />
            <span
              className={`mono text-[0.7rem] tracking-[0.18em] uppercase transition-opacity duration-300 ${
                tagShow ? 'opacity-100' : 'opacity-0'
              }`}
            >
              {TAGLINES[tagIdx]}
            </span>
          </div>

          <h2 className="serif text-[2.7rem] sm:text-[3.2rem] lg:text-[3.6rem] leading-[0.99] tracking-[-0.03em] mb-8">
            Start with <span className="italic text-[var(--text-2)]">one piece of it.</span>
          </h2>

        {/* Composer */}
        <div className="relative text-left rounded-2xl border border-[var(--border-2)] bg-[var(--surface)] focus-within:border-[var(--accent)] transition-colors shadow-[0_20px_60px_-20px_var(--shadow-lift)]">
          <textarea
            id="loreto-composer"
            ref={taRef}
            value={prompt}
            onChange={(e) => { setPrompt(e.target.value); grow(); }}
            onKeyDown={onKey}
            rows={2}
            placeholder={file
              ? 'Add anything Loreto should know about this file (optional)…'
              : (narrow ? PLACEHOLDERS_NARROW : PLACEHOLDERS)[mode]}
            className="w-full bg-transparent resize-none px-5 pt-5 pb-2 text-[1.02rem] leading-[1.6] text-[var(--text)] placeholder:text-[var(--text-3)] focus:outline-none"
          />

          {fileError && (
            <div className="px-5 pb-1">
              <span className="inline-flex items-center gap-2 max-w-full rounded-full border border-[var(--border-2)] bg-[var(--bg)] px-3 py-1.5 text-[0.82rem] text-[var(--accent)]">
                {fileError}
              </span>
            </div>
          )}

          {/* attached file chip */}
          {file && (
            <div className="px-5 pb-1">
              <span className="inline-flex items-center gap-2 max-w-full rounded-full border border-[var(--border-2)] bg-[var(--bg)] pl-3 pr-2 py-1.5 text-[0.82rem] text-[var(--text-2)]">
                <FileUp size={13} className="text-[var(--accent)] shrink-0" />
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ''; }}
                  aria-label="Remove file"
                  className="shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[var(--text-3)] hover:text-[var(--text)] hover:bg-[var(--surface-2)] transition-colors"
                >
                  <X size={13} />
                </button>
              </span>
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            onChange={onPickFile}
            className="hidden"
          />

          <div className="flex flex-wrap items-center gap-2 px-3 pb-3 pt-1 sm:flex-nowrap sm:justify-between sm:gap-3">
            {/* Left: what you can bring in. Attaching a file and connecting a
                repository are the same kind of act — naming a source — so they
                sit together, away from the controls that decide what gets built
                out of it.

                On a phone this drops to the second row and the submit button is
                pushed to the far end of it, because the three artifact toggles
                need the full width and squeezing a round button in beside them
                left it half off the card. */}
            <div className="flex items-center gap-2 order-2 sm:order-1">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                aria-label="Attach a file"
                title="Attach a PDF, image, or HTML file"
                className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-[var(--border-2)] text-[var(--text-2)] hover:text-[var(--text)] hover:border-[var(--text-3)] transition-colors"
              >
                <Paperclip size={17} />
              </button>
              {/* Here rather than only on /generate: someone deciding whether
                  this is worth an account needs to see that it reads private
                  repositories, and the dot is how they will later tell that it
                  is connected. Signed out it is unlit and leads to sign-in. */}
              <GithubConnect
                variant="icon"
                onNeedsAuth={() => { setPostLoginRedirect('/dashboard/generate'); navigate('/dashboard'); }}
                onPickRepo={(url) => { setPrompt(url); setMode('skill'); grow(); }}
                onSingleRepo={(url) => {
                  // One shared repository and an empty box: there is nothing to
                  // choose between, so choose it.
                  setPrompt((cur) => (cur.trim() ? cur : url));
                  grow();
                }}
              />
            </div>

            {/* What comes out. First on a phone, where it is the widest thing
                here and reads as the question being asked. */}
            <div className="order-1 w-full sm:order-2 sm:w-auto">
              <div className="flex items-center gap-1 rounded-full border border-[var(--border-2)] bg-[var(--bg)] p-1">
                {MODES.map(({ id, Icon, label }) => {
                  const active = id === mode;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setMode(id)}
                      aria-pressed={active}
                      className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-full px-2.5 sm:px-3 py-1.5 text-[0.74rem] sm:text-[0.78rem] transition-colors ${
                        active
                          ? 'bg-[var(--surface-2)] text-[var(--text)]'
                          : 'text-[var(--text-3)] hover:text-[var(--text-2)]'
                      }`}
                    >
                      <Icon size={13} className={`shrink-0 ${active ? 'text-[var(--accent)]' : ''}`} /> {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="button"
              onClick={submit}
              disabled={checking}
              aria-label="Build it"
              className="order-3 ml-auto shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-[var(--text)] text-[var(--bg)] hover:bg-[var(--accent)] transition-colors disabled:opacity-70"
            >
              {checking ? <Loader2 size={18} className="animate-spin" /> : <ArrowUp size={18} />}
            </button>
          </div>
        </div>

          {/* example prompts */}
          <div className="mt-5 flex items-center justify-start gap-2 overflow-x-auto sm:flex-wrap
                          -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {EXAMPLES.map((ex) => (
              <button
                key={ex.text}
                type="button"
                onClick={() => applyExample(ex)}
                className="shrink-0 inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[var(--border)] bg-[var(--bg-2)] px-3.5 py-2 text-[0.82rem] text-[var(--text-2)] hover:border-[var(--border-2)] hover:text-[var(--text)] transition-colors"
              >
                <span className="shrink-0 flex items-center"><ex.Icon /></span> {ex.text}
              </button>
            ))}
          </div>

          {/* One line on a phone. Stacked, these three ran under the floating
              WebMCP badge and pushed the carousel off a second screen — and the
              third is a detail nobody needs before they have pressed anything. */}
          <div className="mt-8 flex items-center justify-start gap-x-6 gap-y-2 flex-wrap text-[var(--text-3)] mono text-[0.68rem] tracking-[0.16em] uppercase">
            <span className="inline-flex items-center gap-2"><Sparkles size={12} className="text-[var(--accent)]" /> Free to start</span>
            <span className="hidden sm:inline-flex items-center gap-2"><FileText size={12} className="text-[var(--accent)]" /> Drops into .claude/skills/</span>
            <span className="hidden sm:inline-flex items-center gap-2"><Terminal size={12} className="text-[var(--accent)]" /> Or run it as a CLI</span>
            <span className="sm:hidden inline-flex items-center gap-2"><FileText size={12} className="text-[var(--accent)]" /> .claude/skills/</span>
          </div>

          <StarterGallery onPick={pickStarter} />
        </div>

      </div>
    </section>
  );
}
